import React, { useImperativeHandle, useEffect, useState, forwardRef } from 'react';
import type { AnnotationShape, TextShape } from './types';
import { SvgCanvas } from './components/SvgCanvas';
import { useContainerSize } from './hooks/useContainerSize';
import { exportSvg } from './utils/svgExport';
import { rasterizeAnnotation } from './utils/rasterize';

export interface AnnotationViewerProps {
  targetRef: React.RefObject<HTMLElement | null>;
  shapes: AnnotationShape[];
  /** Called once after the viewer has mounted and the API is available. */
  onMounted?: () => void;
}

export interface AnnotationViewerAPI {
  toSvg: (width: number, height: number) => string;
  rasterize: (width: number, height: number) => Promise<HTMLCanvasElement>;
}

export const AnnotationViewer = forwardRef<AnnotationViewerAPI, AnnotationViewerProps>(
  function AnnotationViewer({ targetRef, shapes, onMounted }, ref) {
    const containerSize = useContainerSize(targetRef);

    // Detect CSS scale from ancestor transforms (e.g. image viewer zoom).
    // The image viewer sets data-zoom on the annotation root when zoom changes.
    // Text fontSize must be multiplied by zoom so it scales with the container.
    const [zoom, setZoom] = useState(1);
    useEffect(() => {
      const el = targetRef.current;
      if (!el) return;

      const readZoom = () => {
        const z = parseFloat(el.dataset.zoom || '1') || 1;
        setZoom((prev) => Math.abs(prev - z) < 0.001 ? prev : z);
      };

      readZoom();

      if (typeof MutationObserver !== 'undefined') {
        const obs = new MutationObserver(readZoom);
        obs.observe(el, { attributes: true, attributeFilter: ['data-zoom'] });
        return () => obs.disconnect();
      }
    }, [targetRef]);

    useImperativeHandle(
      ref,
      () => ({
        toSvg: (width: number, height: number) => {
          return exportSvg(shapes, width, height);
        },
        rasterize: (width: number, height: number) => rasterizeAnnotation(shapes, width, height),
      }),
      [shapes],
    );

    // Fire onMounted callback once after the first render commit.
    useEffect(() => { onMounted?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (containerSize.width === 0 || shapes.length === 0) return null;

    // Compute a proportional stroke width in viewBox units so annotations
    // stay visible when zoomed out or displayed small.
    //
    // The SVG viewBox is 0-1 (normalized). A strokeWidth of S in viewBox
    // units renders as S * containerSize.width layout pixels, then scaled
    // by the CSS zoom transform to S * containerSize.width * zoom screen
    // pixels.
    //
    // To get TARGET_PX screen pixels: S = TARGET_PX / (width * zoom).
    // This produces a constant apparent thickness on screen: thicker in
    // viewBox units when zoomed out (so shapes stay visible), thinner
    // when zoomed in (so strokes don't obscure detail).
    const TARGET_SCREEN_PX = 4;
    const screenWidth = containerSize.width * zoom;
    // Cap at 1.5% of viewBox width so strokes don't overwhelm small
    // annotations when zoomed far out.
    const MAX_VIEWBOX_STROKE = 0.015;
    const scaledStrokeWidth = screenWidth > 0
      ? Math.min(TARGET_SCREEN_PX / screenWidth, MAX_VIEWBOX_STROKE)
      : 0.002;

    return (
      <div className="kollab-annotation-overlay kollab-annotation-overlay--viewer">
        <SvgCanvas
          shapes={shapes}
          containerWidth={containerSize.width}
          containerHeight={containerSize.height}
          scaledStrokeWidth={scaledStrokeWidth}
        />
        {/* Text display overlays */}
        {shapes
          .filter((s): s is TextShape => s.type === 'text')
          .map((shape) => (
            <div
              key={`text-${shape.id}`}
              className="kollab-annotation-text-display"
              style={{
                position: 'absolute',
                left: `${shape.x * 100}%`,
                top: `${shape.y * 100}%`,
                width: `${shape.width * 100}%`,
                height: `${shape.height * 100}%`,
                fontSize: `${shape.fontSize * zoom}px`,
                color: shape.color,
                fontFamily: 'sans-serif',
                lineHeight: 1.3,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                padding: `${2 * zoom}px ${4 * zoom}px`,
                boxSizing: 'border-box',
                overflow: 'hidden',
                pointerEvents: 'none',
                transform: shape.rotation ? `rotate(${shape.rotation}deg)` : undefined,
                transformOrigin: 'center center',
              }}
            >
              {shape.text}
            </div>
          ))}
      </div>
    );
  },
);
